/*
Page-Tracking für die Fehlerseiten (404, 410, 500).

Ohne diese Komponente waren die Fehlerseiten in Matomo unsichtbar: Der Page View
trug den generischen Root-Title ("Heute in Hamburg") — Fehler-Hits waren nicht von
regulären Seitenaufrufen zu unterscheiden.

Getrackt wird ausschließlich als Page View (kein Event-Tracking), mit dem Title
`Error <Fehlercode>` laut Ticket-Vorgabe plus `setCustomUrl` mit der aufgerufenen
URL. Im Pages-Report hängen damit alle Fehler-Hits unter einem Zweig pro
Fehlercode; die kaputte URL steht als Page-URL daran, den Referrer liefert Matomo
ohnehin pro Visit.

Der Page View selbst kommt normalerweise vom Matomo-Bootstrap in TrackingCodes;
diese Komponente setzt dafür nur Title und URL (die `_paq`-Queue wird vor dem
Bootstrap gefüllt) und sendet nur bei Client-Side-Navigation einen eigenen Page
View — siehe claimOwnPageView().

Alles läuft über die `_paq`-Queue, es wird also nichts vor dem Statistik-Consent
gesendet. Eine Fehlerseite, die vor der Consent-Entscheidung wieder verlassen wird,
taucht deshalb in keinem Report auf — bewusste Lücke, siehe docs/tracking-consent.md.
*/

'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  MATOMO_METHODS,
  claimOwnPageView,
  getTrackedUrl,
  pushMatomo,
} from '@/utils/matomo';

export type ErrorPageType = '404' | '410' | '500';

const DOCUMENT_TITLES: Record<ErrorPageType, string> = {
  '404': 'Error 404 – Seite nicht gefunden | Heute in Hamburg',
  '410': 'Error 410 – Seite nicht mehr verfügbar | Heute in Hamburg',
  '500': 'Error 500 – hier löppt irgendwas nicht richtig | Heute in Hamburg',
};

export default function ErrorPageTracking({ errorType }: { errorType: ErrorPageType }) {
  const pathname = usePathname();

  useEffect(() => {
    document.title = DOCUMENT_TITLES[errorType];

    pushMatomo(
      [MATOMO_METHODS.SET_DOCUMENT_TITLE, `Error ${errorType}`],
      [MATOMO_METHODS.SET_CUSTOM_URL, getTrackedUrl()],
    );

    if (claimOwnPageView()) {
      pushMatomo([MATOMO_METHODS.TRACK_PAGE_VIEW]);
    }
  }, [pathname, errorType]);

  return null;
}
